import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { DeleteWithReasonModal } from '@client/components/_shared/DeleteWithReasonModal';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { User } from '@client/components/_ui/display/User';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { ImportRegisterRecordsModal } from '@client/components/registers/ImportRegisterRecordsModal';
import { NewRecordDrawer } from '@client/components/registers/NewRecordDrawer';
import { RegisterBuilderDrawer } from '@client/components/registers/RegisterBuilderDrawer';
import { RegisterRecordFilters } from '@client/components/registers/RegisterRecordFilters';
import { RegisterSnapshotView } from '@client/components/registers/RegisterSnapshotView';
import { recordColumnSignature } from '@client/components/registers/record-column-signature';
import { RecordValue, type StoredRecordValue } from '@client/components/registers/record-value';
import { buildRegisterToolbarState } from '@client/components/registers/register-toolbar-logic';
import { SaveSnapshotModal } from '@client/components/registers/SaveSnapshotModal';
import { SnapshotSchedulesDrawer } from '@client/components/registers/SnapshotSchedulesDrawer';
import { formatSnapshotCapturedDate } from '@client/components/registers/snapshot-capture-display';
import { useBackgroundRegisterImportWatcher } from '@client/components/registers/useBackgroundRegisterImportWatcher';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, type PrismaTableColumn, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { useTitleCacheStorage } from '@client/providers/TitleCacheProvider';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { downloadBlob } from '@client/utils';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Select, Skeleton, Switch, Tag, Tooltip } from 'antd';
import { CalendarClock, Camera, Download, Pencil, RotateCcw, Trash2, Upload } from 'lucide-react';
import { type MouseEvent, useCallback, useMemo, useState } from 'react';

// CUR-644 phase 3 — the records list (REG-DL*): usePrismaTable chrome, dynamic
// Show-as-column columns, type-derived filters, Show deleted (REG-B6), New
// record drawer. Sorting is shell-columns-only (plan D2).

export const Route = createFileRoute('/console/$orgId/compliance/registers/$registerId/')({
    component: RegisterViewPage,
});

type QueryParams = RouterInputs['register']['listRegisterRecords'];
type RecordRow = RouterOutputs['register']['listRegisterRecords']['rows'][number];
type RecordOrderBy = NonNullable<QueryParams['orderBy']>;
type RegisterDetail = RouterOutputs['register']['getRegister'];

/** JSON stringify tolerant of the bigint values filter wheres can carry. */
function stableStringify(value: unknown): string {
    return JSON.stringify(value, (_key, item) => (typeof item === 'bigint' ? `${item}n` : item));
}

function useRecordColumns({
    register,
    canUpdate,
    onDelete,
    onRestore,
    restorePendingId,
}: {
    register: RegisterDetail;
    canUpdate: boolean;
    onDelete: (row: RecordRow) => void;
    onRestore: (row: RecordRow) => void;
    restorePendingId: bigint | null;
}): readonly PrismaTableColumn<RecordRow, RecordOrderBy, any>[] {
    const orgId = useOrgId();
    const { formatDate, formatISODate } = useFormatter();

    return useMemo(() => {
        const columnHelper = createColumnHelper<RecordRow, RecordOrderBy>();
        const shellByKey = new Map(register.Fields.filter((f) => f.systemKey !== null).map((f) => [f.systemKey, f]));
        const dynamicFields = register.Fields.filter(
            (field) => field.systemKey === null && field.showAsColumn && !field.isArchived,
        ).sort((a, b) => a.order - b.order);

        return [
            columnHelper.accessor('ref', {
                id: 'ref',
                header: 'Ref',
                sortable: 'ref',
                width: 90,
                cell: ({ row }) => <span className="font-mono text-muted-foreground text-xs">#{row.ref}</span>,
            }),
            columnHelper.accessor('title', {
                id: 'title',
                header: shellByKey.get('TITLE')?.label ?? 'Name',
                sortable: 'title',
                cell: ({ row }) =>
                    row.isDeleted ? (
                        <span className="inline-flex items-center gap-2 text-muted-foreground">
                            {row.title}
                            <Tooltip
                                // Hover alone strands keyboard users on a focusable chip that
                                // never reveals its content (WCAG 1.4.13).
                                trigger={['hover', 'focus']}
                                title={`Deleted by ${row.DeletedBy?.fullName ?? 'unknown'} on ${
                                    row.deletedAt ? formatDate(row.deletedAt) : 'unknown'
                                }. Reason: ${row.deletionReason ?? '-'}`}
                            >
                                {/* red preset, outlined: the semantic error tag's text measures
                                    2.99:1 (light) / 4.01:1 (dark) on its tint — below AA 4.5:1
                                    for 11px text; red-outlined measures ≥5:1 in both themes. */}
                                <Tag color="red" variant="outlined" tabIndex={0} className="m-0 text-2xs leading-none">
                                    Deleted
                                </Tag>
                            </Tooltip>
                        </span>
                    ) : (
                        <span>{row.title}</span>
                    ),
            }),
            columnHelper.accessor('ownerId', {
                id: 'owner',
                header: shellByKey.get('OWNER')?.label ?? 'Owner',
                sortable: 'Owner.firstName',
                width: 170,
                cell: ({ row }) =>
                    row.Owner ? (
                        <User userId={row.Owner.userId} photoUrl={row.Owner.photoUrl} name={row.Owner.fullName} />
                    ) : (
                        <span className="text-subtle-foreground">-</span>
                    ),
            }),
            ...dynamicFields.map((field) =>
                columnHelper.display({
                    id: `field:${field.fieldId}`,
                    header: field.label,
                    // Dynamic JSON columns display and filter but never sort (plan D2/Q10).
                    cell: ({ row }) => (
                        <RecordValue
                            field={field}
                            value={(row.data as Record<string, StoredRecordValue>)?.[field.fieldId]}
                        />
                    ),
                }),
            ),
            columnHelper.accessor('nextReview', {
                id: 'nextReview',
                header: shellByKey.get('NEXT_REVIEW')?.label ?? 'Next review',
                sortable: 'nextReview',
                width: 130,
                // The @db.Date value is UTC midnight — format its UTC calendar
                // day (like the date-typed custom values), or users west of
                // UTC see the previous day.
                cell: ({ row }) =>
                    row.nextReview ? (
                        <span>{formatISODate(row.nextReview.toISOString().slice(0, 10))}</span>
                    ) : (
                        <span className="text-subtle-foreground">-</span>
                    ),
            }),
            columnHelper.actions({
                cell: ({ row }) =>
                    row.isDeleted ? (
                        canUpdate ? (
                            <Button
                                size="small"
                                icon={<RotateCcw />}
                                loading={restorePendingId === row.id}
                                onClick={() => onRestore(row)}
                            >
                                Restore
                            </Button>
                        ) : null
                    ) : (
                        <Space size="small">
                            {/* Not a View button: that called the very same
                                openRecord as the row click, so it was a control
                                that did nothing the row did not already do. This
                                is what claims, complaints and incidents put in
                                this slot — it adds the one thing a row click
                                cannot do, and as a real anchor it is also the
                                keyboard route into a record (the row itself is a
                                <tr onClick> with nothing focusable in it). */}
                            <Button.OpenInNewTab
                                to="/console/$orgId/compliance/registers/$registerId/records/$recordId"
                                params={{ orgId, registerId: register.id, recordId: row.id }}
                                tooltip="Open in new tab"
                                // A Tooltip contributes no accessible name, so without
                                // this the anchor announces as an unnamed link — beside
                                // a Delete button that names itself. The reference makes
                                // it unambiguous when every row repeats the action.
                                aria-label={`Open record #${row.ref} in new tab`}
                            />
                            {canUpdate && (
                                <Button.Icon
                                    icon={<Trash2 />}
                                    className="text-status-danger"
                                    aria-label="Delete record"
                                    onClick={() => onDelete(row)}
                                />
                            )}
                        </Space>
                    ),
            }),
        ] as const;
    }, [
        register.Fields,
        register.id,
        orgId,
        canUpdate,
        onDelete,
        onRestore,
        restorePendingId,
        formatDate,
        formatISODate,
    ]);
}

function RegisterRecordsTable({
    register,
    viewSnapshotId,
    setViewSnapshotId,
    onEditRegister,
}: {
    register: RegisterDetail;
    /** REG-SN6: null = the live register ("Current"); an id = the frozen view. */
    viewSnapshotId: bigint | null;
    setViewSnapshotId: (id: bigint | null) => void;
    /** Opens the builder, which is owned by the page (it outlives this table). */
    onEditRegister: () => void;
}) {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const { can } = usePermissions();
    const { trpcClient, trpcUtils } = useTrpc();
    const canUpdate = can('register:update');

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<RecordRow | null>(null);
    const [snapshotModalOpen, setSnapshotModalOpen] = useState(false);
    const [schedulesOpen, setSchedulesOpen] = useState(false);
    const [input, setInput] = usePathState<Omit<QueryParams, 'registerId'>>('record-query-params', {
        where: {},
        orderBy: { ref: 'desc' },
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
        showDeleted: false,
    });

    const {
        data,
        isPending,
        error: listError,
        refetch: refetchList,
    } = trpc.register.listRegisterRecords.useQuery({ ...input, registerId: register.id });
    const {
        data: snapshotsData,
        error: snapshotsError,
        refetch: refetchSnapshots,
    } = trpc.register.listRegisterSnapshots.useQuery({ registerId: register.id });
    const snapshots = snapshotsData?.rows ?? [];
    const viewingSnapshot =
        viewSnapshotId === null ? null : (snapshots.find((snapshot) => snapshot.id === viewSnapshotId) ?? null);

    // Stable identity: also the background import watcher's onCompleted (an
    // effect dependency in the hook).
    const invalidate = useCallback(() => {
        void trpcUtils.register.listRegisterRecords.invalidate();
        void trpcUtils.register.listRegisters.invalidate();
    }, [trpcUtils]);

    // Keeps polling an import job whose modal was closed while it was still
    // queued/running, so the outcome is surfaced and the table refreshes.
    const { track: trackDismissedImport } = useBackgroundRegisterImportWatcher(register.id, invalidate);

    const deleteRecord = trpc.register.deleteRegisterRecord.useMutation({
        onSuccess: () => {
            invalidate();
            message.success('Record moved to the bin');
            setDeleteTarget(null);
        },
        onError: (error) => errorMessage.show(error),
    });

    const restoreRecord = trpc.register.restoreRegisterRecord.useMutation({
        onSuccess: () => {
            invalidate();
            message.success('Record restored');
        },
        onError: (error) => errorMessage.show(error),
    });

    // REG-E1 — every non-archived field + reference; round-trips through import.
    // When a snapshot is selected the download comes from the FROZEN payload
    // instead, with the linked-items column (REG-SN6, JLo answer 1).
    // BOM prefix so Excel opens the UTF-8 file with accents intact.
    const handleExport = async () => {
        try {
            setIsExporting(true);
            const result =
                viewSnapshotId === null
                    ? await trpcClient.register.exportRegisterRecords.query({ registerId: register.id })
                    : await trpcClient.register.exportRegisterSnapshot.query({
                          registerId: register.id,
                          snapshotId: viewSnapshotId,
                      });
            downloadBlob(new Blob(['\uFEFF' + result.csv], { type: 'text/csv;charset=utf-8;' }), result.filename);
            message.success(`Exported ${result.count} record${result.count === 1 ? '' : 's'}`);
        } catch (error) {
            errorMessage.show(error);
        } finally {
            setIsExporting(false);
        }
    };

    // The event is what lets cmd/ctrl+click open a new tab — useNavigate reads
    // metaKey/ctrlKey off it (useNavigate.ts) and falls back to an in-place
    // navigation without it. Claims passes it; registers did not, which left it
    // the one list page where a record could not be opened in a new tab at all.
    const openRecord = (recordId: bigint, event?: MouseEvent) =>
        void navigate(
            {
                to: '/console/$orgId/compliance/registers/$registerId/records/$recordId',
                params: { orgId, registerId: register.id, recordId },
            },
            event,
        );

    const columns = useRecordColumns({
        register,
        canUpdate,
        onDelete: setDeleteTarget,
        onRestore: (row) => restoreRecord.mutate({ registerId: register.id, recordId: row.id }),
        restorePendingId: restoreRecord.isPending ? (restoreRecord.variables?.recordId ?? null) : null,
    });

    const tableProps = usePrismaTable({
        data: data?.rows ?? [],
        total: data?.total ?? 0,
        loading: isPending,
        columns,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: setInput,
        pagination: {
            position: 'both',
            showSizeChanger: true,
        },
    });

    // REG-SN2: editing, import and delete are disabled while viewing a
    // snapshot — the toolbar drops those actions and the table is replaced by
    // the read-only frozen view. Decision table in register-toolbar-logic.ts
    // (unit-tested).
    const isViewingSnapshot = viewSnapshotId !== null;
    const toolbar = buildRegisterToolbarState({
        canUpdate,
        isViewingSnapshot,
        snapshotName: viewingSnapshot?.name ?? null,
    });

    return (
        // One element, not a fragment: the page stacks its children with `gap-4`,
        // and a fragment would make the toolbar and the table two separate flex
        // children — the parent gap then stacks on TableToolbar's own `mb-4` and
        // the toolbar sits 32px off its table, twice what every other list page
        // uses (claims, complaints render TableToolbar with no flex wrapper).
        <div>
            <TableToolbar
                // TableToolbar's row is `flex justify-between` with no wrap, and it
                // gives the filters `flex-1` (basis 0%). This register's bar carries
                // more actions than any other list page, so below ~1100px the
                // actions win the space and the filters collapse under their own
                // content instead of moving to a second line. Wrapping the row is a
                // local override — changing TableToolbar itself would land on every
                // table page in the app and is a separate change. `items-start`
                // keeps the actions on the first line when applied filter chips
                // wrap the filters block to several rows — `items-center` sank
                // them to its vertical middle. TableToolbar renders those actions
                // in an Ant Design Space (`.ant-space`), whose centred alignment
                // re-centres them inside the now top-aligned row when given this
                // minimum height. The `38px` intentionally mirrors TableToolbar's
                // row `min-h-[38px]`; keep the two values in sync.
                className="[&>div>.ant-space]:min-h-[38px] [&>div]:flex-wrap [&>div]:items-start"
                filters={
                    toolbar.showFilters ? (
                        <RegisterRecordFilters
                            fields={register.Fields}
                            onChange={({ where, search, dataRanges }) =>
                                setInput((prev) => {
                                    // The filter component replays restored filters on
                                    // mount; only a genuine change resets the persisted
                                    // page offset (grid persistence, CUR-451).
                                    const unchanged =
                                        stableStringify(prev.where ?? {}) === stableStringify(where ?? {}) &&
                                        (prev.search || undefined) === (search || undefined) &&
                                        stableStringify(prev.dataRanges ?? []) === stableStringify(dataRanges ?? []);
                                    return unchanged ? prev : { ...prev, where, search, dataRanges, offset: 0 };
                                })
                            }
                        />
                    ) : undefined
                }
                actions={
                    // `wrap`: this bar carries up to five actions plus the bin toggle
                    // and the snapshot picker. Without it they overflow their row
                    // below ~1100px and paint on top of each other instead of moving
                    // to a second line.
                    <Space wrap className="justify-end">
                        {/* Beside the actions, not among the filters: the filter bar
                            grows as chips are added, so a toggle sharing that row
                            wraps under the search box and reads as attached to
                            nothing. DocumentLibrary puts the same Show deleted
                            control in this slot. The wrapping <label> is what gives
                            the Switch its accessible name. The extra right margin
                            sets the passive toggle slightly apart from the action
                            cluster it precedes. */}
                        {toolbar.showShowDeleted && (
                            <label className="mr-2 flex cursor-pointer items-center gap-2 text-muted-foreground text-xs">
                                Show deleted
                                <Switch
                                    size="small"
                                    checked={input.showDeleted ?? false}
                                    onChange={(showDeleted) =>
                                        setInput((prev) => ({ ...prev, showDeleted, offset: 0 }))
                                    }
                                />
                            </label>
                        )}
                        {/* A failed snapshot list must not masquerade as an
                            empty history — offer a compact retry instead of
                            silently hiding the viewer. */}
                        {snapshotsError && (
                            <Button size="small" icon={<RotateCcw />} onClick={() => void refetchSnapshots()}>
                                Retry snapshots
                            </Button>
                        )}
                        {snapshots.length > 0 && (
                            <Select
                                aria-label="View a snapshot"
                                value={viewSnapshotId?.toString() ?? 'current'}
                                onChange={(value) => setViewSnapshotId(value === 'current' ? null : BigInt(value))}
                                popupMatchSelectWidth={false}
                                className="min-w-44"
                                prefix={<Camera size={13} className="text-subtle-foreground" />}
                                options={[
                                    { value: 'current', label: 'Current' },
                                    ...snapshots.map((snapshot) => ({
                                        value: snapshot.id.toString(),
                                        // Capture DAY in the org's reporting zone, so
                                        // this label, the snapshot banner and the
                                        // exported CSV filename all name the same day
                                        // for a capture taken near midnight.
                                        label: `${snapshot.name} · ${formatSnapshotCapturedDate(snapshot.capturedAt, snapshot.timezoneAtCapture)}`,
                                    })),
                                ]}
                            />
                        )}
                        {/* Icon-only, and deliberately not a repeat of the overflow
                            kebab this replaced: a MoreHorizontal glyph names nothing,
                            whereas Download and Upload each state their action. They
                            are mirror images sitting side by side though, and import
                            writes data, so the tooltip is not optional — it is the
                            only text either button has. Export keeps its dynamic
                            snapshot label (REG-SN6) in the tooltip.

                            `secondary` so they sit as bordered peers of the
                            labelled Save snapshot / Edit register actions
                            beside them, rather than the borderless
                            toolbar-utility treatment. */}
                        <Tooltip title={isExporting ? 'Exporting…' : toolbar.exportLabel}>
                            <Button
                                type="secondary"
                                iconOnly
                                icon={<Download />}
                                aria-label={toolbar.exportLabel}
                                loading={isExporting}
                                onClick={() => void handleExport()}
                            />
                        </Tooltip>
                        {toolbar.showImport && (
                            <Tooltip title="Import CSV">
                                <Button
                                    type="secondary"
                                    iconOnly
                                    icon={<Upload />}
                                    aria-label="Import CSV"
                                    onClick={() => setImportOpen(true)}
                                />
                            </Tooltip>
                        )}
                        {/* REG-SN6: a visible toolbar action, never in the overflow menu. */}
                        {toolbar.showSaveSnapshot && (
                            <Button type="secondary" icon={<Camera />} onClick={() => setSnapshotModalOpen(true)}>
                                Save snapshot
                            </Button>
                        )}
                        {/* CUR-722. Icon-only beside Save snapshot: same family of
                            action, but the manual capture stays the labelled one. */}
                        {toolbar.showScheduleSnapshots && (
                            <Tooltip title="Snapshot schedules">
                                <Button
                                    type="secondary"
                                    iconOnly
                                    icon={<CalendarClock />}
                                    aria-label="Snapshot schedules"
                                    onClick={() => setSchedulesOpen(true)}
                                />
                            </Tooltip>
                        )}
                        {toolbar.showEditRegister && (
                            <Button type="secondary" icon={<Pencil />} onClick={onEditRegister}>
                                Edit register
                            </Button>
                        )}
                        {toolbar.showNewRecord && (
                            <Button.Add label="New record" variant="primary" onClick={() => setDrawerOpen(true)} />
                        )}
                    </Space>
                }
            />

            {/* Snapshot mode replaces the live table entirely; the frozen view
                owns its own loading/error states. */}
            {isViewingSnapshot ? (
                <RegisterSnapshotView
                    registerId={register.id}
                    snapshotId={viewSnapshotId}
                    onBackToCurrent={() => setViewSnapshotId(null)}
                />
            ) : listError ? (
                <InlineQueryError
                    error={listError}
                    onRetry={() => void refetchList()}
                    title="Couldn't load the records"
                />
            ) : (
                <PrismaTable
                    {...tableProps}
                    rowClassName={(row: RecordRow) => (row.isDeleted ? 'opacity-55' : 'cursor-pointer')}
                    onRow={(row: RecordRow) => ({
                        onClick: (event) => {
                            // Binned rows are read-only except Restore (REG-B6).
                            if (row.isDeleted) return;
                            openRecord(row.id, event);
                        },
                    })}
                />
            )}

            <SnapshotSchedulesDrawer
                open={schedulesOpen}
                registerId={register.id}
                onClose={() => setSchedulesOpen(false)}
            />

            <SaveSnapshotModal
                open={snapshotModalOpen}
                registerId={register.id}
                onClose={() => setSnapshotModalOpen(false)}
            />

            <NewRecordDrawer
                open={drawerOpen}
                registerId={register.id}
                fields={register.Fields}
                onClose={() => setDrawerOpen(false)}
            />

            <ImportRegisterRecordsModal
                open={importOpen}
                registerId={register.id}
                registerName={register.name}
                onClose={() => setImportOpen(false)}
                onSuccess={invalidate}
                onDismissPending={trackDismissedImport}
            />

            <DeleteWithReasonModal
                open={deleteTarget !== null}
                title={deleteTarget ? `Delete ${deleteTarget.title}?` : 'Delete record'}
                confirmLabel="Delete"
                description="It stays available under Show deleted, with your name and the date, and can be restored. A reason is required."
                isLoading={deleteRecord.isPending}
                onConfirm={(reason) => {
                    if (!deleteTarget) return;
                    deleteRecord.mutate({ registerId: register.id, recordId: deleteTarget.id, reason });
                }}
                onClose={() => setDeleteTarget(null)}
            />
        </div>
    );
}

function RegisterViewPage() {
    const { registerId } = Route.useParams();
    const titleCache = useTitleCacheStorage();
    const router = useRouter();
    const { trpcUtils } = useTrpc();
    const [builderOpen, setBuilderOpen] = useState(false);
    // REG-SN6: null = the live register ("Current"); an id = the frozen view.
    // Owned HERE, not by the table, which remounts on a column change and would
    // reset it. The builder is owned here for the same reason.
    const [viewSnapshotId, setViewSnapshotId] = useState<bigint | null>(null);
    const { data, isPending, error, refetch } = trpc.register.getRegister.useQuery({ id: registerId });

    return (
        <PageController route={Route}>
            {error ? (
                <InlineQueryError error={error} onRetry={() => void refetch()} title="Couldn't load the register" />
            ) : isPending || !data ? (
                <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
                <div className="flex flex-col gap-4">
                    {/* The register name is the page title (route `ui.title`), so
                        this is only what the header cannot carry: `ui.subtitle` is
                        typed `string`, and resolving a per-register description
                        into it would mean an async beforeLoad that takes over the
                        loading and error states this page owns. Each item gets its
                        own line — the regulation is a classification, not a prefix
                        to the sentence that follows it. */}
                    {(data.regulation || data.description) && (
                        <div className="flex flex-col items-start gap-1">
                            {/* Named, not pilled. `regulation` is nullable free text,
                                and a Tag promises a fixed vocabulary the reader can
                                recognise — which is why an unlabelled pill here read
                                as "what is this?". Every other regulation reference
                                in the platform is text: the Legislation field on an
                                obligation, the Legislation column, and the muted
                                `legislation · section` line on matched obligations. */}
                            {data.regulation && (
                                <span className="text-muted-foreground text-xs">Regulation · {data.regulation}</span>
                            )}
                            {data.description && (
                                <span className="text-muted-foreground text-sm">{data.description}</span>
                            )}
                        </div>
                    )}
                    {/* Keyed on the column set: the builder can add, archive,
                        rename or reorder columns under a mounted table, and the
                        table caches its visible columns from mount. */}
                    <RegisterRecordsTable
                        key={recordColumnSignature(data.Fields)}
                        register={data}
                        viewSnapshotId={viewSnapshotId}
                        setViewSnapshotId={setViewSnapshotId}
                        onEditRegister={() => setBuilderOpen(true)}
                    />
                    <RegisterBuilderDrawer
                        open={builderOpen}
                        editRegister={data}
                        // A rename has to refresh three layers, in this order, or
                        // the heading keeps the old name while the table shows the
                        // new one:
                        //   1. the query, or the title resolver re-reads the old
                        //      name from cache;
                        //   2. the memoised title, keyed on match id + pathname —
                        //      neither of which changes when the name does;
                        //   3. the router, because the resolver only re-runs when
                        //      its route context identity changes. Clearing the
                        //      cache without this leaves nothing to re-resolve —
                        //      verified in the browser: cache-only left the
                        //      heading stale until a full reload.
                        //
                        // The BREADCRUMB cannot be refreshed from here. Breadcrumbs
                        // keys its resolver on `[match.id, titleCache]`, and a
                        // rename changes neither, so the crumb keeps the old name
                        // until the next navigation. Clearing `breadcrumb:` here
                        // would be a no-op dressed up as a fix; correcting it means
                        // changing a shared component every page renders.
                        onSaved={async () => {
                            await trpcUtils.register.getRegister.invalidate({ id: registerId });
                            titleCache.invalidate('ui-title:');
                            await router.invalidate();
                        }}
                        onClose={() => setBuilderOpen(false)}
                    />
                </div>
            )}
        </PageController>
    );
}
